/**
 * 플래그 배지 색상(2026-08-06).
 *
 * 색은 `flag_style` 테이블에 `#RRGGBB` 로 저장된다. **Tailwind 클래스명을 저장하지 않는 이유** =
 * 클래스는 빌드 시 소스 스캔으로 생성되므로 DB 값으로 조립한 클래스명은 번들에 없다(색이 안 먹는다).
 * → 그래서 여기서 **inline style** 을 만든다.
 */

/** 색 미지정 플래그의 기본색(종전 배지와 같은 보라 계열). */
export const DEFAULT_FLAG_COLOR = '#EDE9FE';

/** 설정 화면 팔레트. 배지로 읽히는 밝기대를 골랐다(진한 색은 흰 글씨로 자동 전환된다). */
export const FLAG_COLOR_PRESETS = [
  '#4F46E5', '#2563EB', '#0891B2', '#059669', '#65A30D', '#CA8A04',
  '#EA580C', '#DC2626', '#DB2777', '#9333EA', '#475569', '#111827',
  '#E0E7FF', '#DBEAFE', '#CFFAFE', '#D1FAE5', '#ECFCCB', '#FEF3C7',
  '#FFEDD5', '#FEE2E2', '#FCE7F3', '#EDE9FE', '#E2E8F0', '#F3F4F6',
];

/** `#RRGGBB` → {r,g,b}. 형식이 틀리면 null(호출부가 기본색으로 처리). */
const parseHex = (hex) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex ?? '').trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};

/**
 * 배경색에 맞는 글자색. 밝은 배경이면 검정, 어두우면 흰색.
 * 담당자가 아무 색이나 고를 수 있으므로 **대비를 코드가 보장**해야 한다
 * (고정 글자색이면 진한 배경에서 글씨가 안 보인다).
 * 기준 = WCAG 상대 명도 0.5.
 */
export const textColorFor = (bg) => {
  const c = parseHex(bg);
  if (!c) return '#1F2937';
  const lin = (v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  const luminance = 0.2126 * lin(c.r) + 0.7152 * lin(c.g) + 0.0722 * lin(c.b);
  return luminance > 0.5 ? '#1F2937' : '#FFFFFF';
};

/**
 * 플래그 배지 inline style.
 * @param color `flag_style.color`. null/미지정/형식오류면 기본색.
 */
export const flagBadgeStyle = (color) => {
  const bg = parseHex(color) ? color : DEFAULT_FLAG_COLOR;
  return { backgroundColor: bg, color: textColorFor(bg) };
};

/** `listFlagStyles()` 응답 → `Map<flagCode, color>`. 색이 없는 플래그는 담지 않는다(기본색 처리). */
export const toFlagColorMap = (styles) => {
  const map = new Map();
  (styles ?? []).forEach((s) => { if (s?.flagCode && s?.color) map.set(s.flagCode, s.color); });
  return map;
};
